import { useState, useEffect, useRef } from 'react';
import { Download, Bot, Loader2, ZoomIn, ChevronDown, Plus, Bold, List, Hash, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Dialog, DialogContent } from '@travellegacy/nexops-design-system';
import { instagramAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { TextFormatter } from '../utils/textFormatter';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface Socio {
  id: number;
  name: string;
  handle: string;
  avatar: string;
  profileId: string; // ID para API
}

interface Theme {
  bg: string;
  textPrimary: string;
  textHandle: string;
}

interface GeneratedPost {
  text: string;
  canvasData?: string; // Base64 da imagem gerada
}

const THEMES = {
  dark: {
    bg: '#0f0f0f',
    textPrimary: '#e8e8e8',
    textHandle: '#666',
  },
  light: {
    bg: '#f5f5f5',
    textPrimary: '#111',
    textHandle: '#666',
  },
};

const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1350; // Formato 4:5 (Instagram)
const FONT_FACE = 'system-ui, -apple-system, sans-serif';

// Temas por perfil
const PROFILE_THEMES: Record<string, string[]> = {
  tarik: [
    'NexOps e automação inteligente',
    'Escalar sem contratar mais',
    'Segurança no mercado de viagens',
    'Processo e organização competitiva',
    'IA aplicada ao negócio',
    'Experiência em tech',
    'Paciência estratégica vs urgência',
    'Viagens pela Europa',
    'Gestão de tempo e prioridades',
    'Execução vs teoria',
  ],
  saulo: [
    'Serviço de alto nível',
    'Liderança e gestão de time',
    'Alinhamento com cliente',
    'Cuidado com detalhes',
    'Obsessão pelo sucesso do cliente',
    'Resultados da operação',
    'Aluno antes de sócio',
    'Bastidores da operação',
    'Fé, casamento e equilíbrio',
    'Rotina de disciplina',
  ],
  gabriela: [
    'Mulher em operação de alto nível',
    'Bastidores da operação',
    'Quando algo dá errado',
    'Delegar viagens com qualidade',
    'Liderança de time',
    'Resultados de clientes mulheres',
    'Sócia e parceira',
    'Viajar com qualidade',
    'Rotina de alto nível',
    'Atendimento a alto padrão',
  ],
  gabriel: [
    'Custo de não decidir',
    'Venda consultiva vs pressão',
    'Processo comercial',
    'Resultados de clientes',
    'Objeções comuns',
    'Relacionamento e parceria',
    'Closer de alto nível',
    'Fé e equilíbrio',
    'Disciplina e resultado',
    'Transparência no fechamento',
  ],
  caio: [
    'Casamento e parceria',
    'Fé e lógica nas decisões',
    'Gestão de colaboradores',
    'Erros que ensinaram',
    'Selecionar sócios e time',
    'Empresário vs empreendedor',
    'Construir mercado inexistente',
    'Clientes empresários ricos',
    'Viagem como estratégia',
    'Princípios inegociáveis',
  ],
};

// Sócios preset - Travel Legacy Team
const DEFAULT_SOCIOS: Socio[] = [
  {
    id: 1,
    name: 'Tárik Wataya',
    handle: '@twataya',
    avatar: '/socios/Wataya.png',
    profileId: 'tarik',
  },
  {
    id: 2,
    name: 'Caio Nascimento',
    handle: '@caionascimento_',
    avatar: '/socios/Caio.png',
    profileId: 'caio',
  },
  {
    id: 3,
    name: 'Saulo Fonseca',
    handle: '@saulofonseca_',
    avatar: '/socios/Saulo.png',
    profileId: 'saulo',
  },
  {
    id: 4,
    name: 'Gabriela Marques',
    handle: '@gabrielamarquesf_',
    avatar: '/socios/Gabriela.png',
    profileId: 'gabriela',
  },
  {
    id: 5,
    name: 'Gabriel Marques',
    handle: '@gabrielmarques.vs',
    avatar: '/socios/Gabriel.png',
    profileId: 'gabriel',
  },
  {
    id: 6,
    name: 'Travel Legacy',
    handle: '@travellegacyoficial',
    avatar: '/socios/Travel.png',
    profileId: 'travel-legacy',
  },
];

export default function PostXPage() {
  const [socios] = useState<Socio[]>(DEFAULT_SOCIOS);
  const [selectedSocio, setSelectedSocio] = useState<Socio | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [currentText, setCurrentText] = useState('');
  const [generatedPosts, setGeneratedPosts] = useState<GeneratedPost[]>([]);
  const [selectedPostIndex, setSelectedPostIndex] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [isManualMode, setIsManualMode] = useState(true); // Modo manual (digitação) vs AI
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Temas disponíveis para o perfil selecionado
  const availableThemes = selectedSocio ? PROFILE_THEMES[selectedSocio.profileId] || [] : [];

  // Quando usuário digita manualmente, criar/atualizar post na galeria
  useEffect(() => {
    if (!selectedSocio || !currentText || !isManualMode) return;

    // Se há um post selecionado, atualizar ele
    if (selectedPostIndex !== null && generatedPosts[selectedPostIndex]) {
      setGeneratedPosts((prevPosts) => {
        const updated = [...prevPosts];
        updated[selectedPostIndex] = { text: currentText };
        return updated;
      });
    }
    // Se não existe post, criar novo
    else if (generatedPosts.length === 0) {
      setGeneratedPosts([{ text: currentText }]);
      setSelectedPostIndex(0);
    }
  }, [currentText, selectedSocio, isManualMode, selectedPostIndex]);

  // Renderizar todos os posts quando necessário
  useEffect(() => {
    if (generatedPosts.length > 0 && selectedSocio) {
      generatedPosts.forEach((post, index) => {
        // Re-renderizar posts quando:
        // - Não tem canvasData ainda
        // - Texto mudou
        // - Tema mudou (sempre re-renderiza para aplicar novo tema)
        renderCanvasToDataURL(selectedSocio, post.text, THEMES[theme], index);
      });
    }
  }, [generatedPosts, selectedSocio, theme]);

  /**
   * Renderiza canvas com formatação (negrito, bullets, hashtags) e retorna base64
   */
  const renderCanvasToDataURL = (
    socio: Socio,
    text: string,
    themeColors: Theme,
    postIndex: number
  ): void => {
    // Criar canvas temporário
    const tempCanvas = document.createElement('canvas');
    const ctx = tempCanvas.getContext('2d');
    if (!ctx) return;

    tempCanvas.width = CANVAS_WIDTH;
    tempCanvas.height = CANVAS_HEIGHT;

    // Background
    ctx.fillStyle = themeColors.bg;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Configurações de texto
    const lineHeight = 54;
    const maxWidth = CANVAS_WIDTH - 120;
    const fontSize = 42;

    // Parse do texto com formatação
    const formattedLines = TextFormatter.parse(text);

    // Calcular altura total do conteúdo
    const textHeight = TextFormatter.calculateHeight(
      formattedLines,
      ctx,
      fontSize,
      lineHeight,
      maxWidth
    );

    const avatarSize = 100;
    const headerHeight = avatarSize;
    const gapBetweenHeaderAndText = 70;
    const totalContentHeight = headerHeight + gapBetweenHeaderAndText + textHeight;
    const contentStartY = (CANVAS_HEIGHT - totalContentHeight) / 2;

    const avatarX = 60;
    const avatarY = contentStartY;

    // Avatar
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, avatarX, avatarY, avatarSize, avatarSize);
      ctx.restore();

      // Nome e Handle - centralizados verticalmente com o avatar
      const nameSize = 38;
      const handleSize = 24;
      const gapBetweenNameAndHandle = 10;
      const textBlockHeight = nameSize + gapBetweenNameAndHandle + handleSize;
      const textStartY = avatarY + (avatarSize - textBlockHeight) / 2;

      // Nome
      ctx.fillStyle = themeColors.textPrimary;
      ctx.font = `bold ${nameSize}px ${FONT_FACE}`;
      ctx.fillText(socio.name, avatarX + avatarSize + 20, textStartY + nameSize);

      // Handle
      ctx.fillStyle = themeColors.textHandle;
      ctx.font = `${handleSize}px ${FONT_FACE}`;
      ctx.fillText(socio.handle, avatarX + avatarSize + 20, textStartY + nameSize + gapBetweenNameAndHandle + handleSize);

      // Renderizar texto formatado
      let currentY = avatarY + avatarSize + gapBetweenHeaderAndText;

      for (const line of formattedLines) {
        // Linha vazia - espaçamento
        if (line.type === 'empty') {
          currentY += lineHeight * 0.5;
          continue;
        }

        // Renderizar elementos da linha
        let currentX = 60;
        const effectiveMaxWidth = line.type === 'bullet' ? maxWidth - 35 : maxWidth;

        for (const element of line.elements) {
          if (element.type === 'bullet') {
            ctx.font = `${fontSize}px ${FONT_FACE}`;
            ctx.fillStyle = themeColors.textPrimary;
            ctx.fillText('• ', currentX, currentY);
            currentX += ctx.measureText('• ').width;
            continue;
          }

          // Configurar fonte baseado no tipo
          if (element.type === 'bold') {
            ctx.font = `bold ${fontSize}px ${FONT_FACE}`;
          } else {
            ctx.font = `${fontSize}px ${FONT_FACE}`;
          }

          // Cor baseada no tipo
          if (element.type === 'hashtag') {
            ctx.fillStyle = '#3b82f6'; // Azul para hashtags
          } else {
            ctx.fillStyle = themeColors.textPrimary;
          }

          // Renderizar o conteúdo do elemento caractere por caractere para wrapping
          const content = element.content;
          let currentWord = '';

          for (let i = 0; i < content.length; i++) {
            const char = content[i];

            if (char === ' ' || i === content.length - 1) {
              // Final da palavra ou último caractere
              const wordToRender = i === content.length - 1 && char !== ' ' ? currentWord + char : currentWord;

              if (wordToRender) {
                const wordWidth = ctx.measureText(wordToRender).width;

                // Verificar se precisa quebrar linha
                if (currentX + wordWidth > 60 + effectiveMaxWidth && currentX > 60) {
                  currentY += lineHeight;
                  currentX = line.type === 'bullet' ? 60 + 35 : 60;
                }

                ctx.fillText(wordToRender, currentX, currentY);
                currentX += wordWidth;
              }

              // Renderizar espaço se não for o último caractere
              if (char === ' ') {
                const spaceWidth = ctx.measureText(' ').width;
                ctx.fillText(' ', currentX, currentY);
                currentX += spaceWidth;
              }

              currentWord = '';
            } else {
              currentWord += char;
            }
          }
        }

        currentY += lineHeight;
      }

      // Salvar canvas data
      const dataURL = tempCanvas.toDataURL();
      setGeneratedPosts((prev) => {
        const updated = [...prev];
        updated[postIndex] = { ...updated[postIndex], canvasData: dataURL };
        return updated;
      });
    };

    img.onerror = () => {
      // Fallback sem avatar
      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.fillStyle = '#333';
      ctx.fillRect(avatarX, avatarY, avatarSize, avatarSize);
      ctx.restore();

      const dataURL = tempCanvas.toDataURL();
      setGeneratedPosts((prev) => {
        const updated = [...prev];
        updated[postIndex] = { ...updated[postIndex], canvasData: dataURL };
        return updated;
      });
    };

    img.src = socio.avatar;
  };

  const handleSocioClick = (socio: Socio) => {
    setSelectedSocio(socio);
    setGeneratedPosts([]); // Limpar posts ao trocar perfil
    setSelectedPostIndex(null);
    setCurrentText('');
    setSelectedTheme(null); // Resetar tema ao trocar perfil
    setIsManualMode(true); // Voltar para modo manual
  };

  const handleExportSpecific = (index: number) => {
    // Exportar um post específico da galeria
    const post = generatedPosts[index];
    if (post?.canvasData) {
      const link = document.createElement('a');
      const handle = selectedSocio?.handle?.replace('@', '') || 'post';
      link.download = `post-${handle}-${index + 1}-${Date.now()}.png`;
      link.href = post.canvasData;
      link.click();
      toast.success(`Post ${index + 1} exportado com sucesso!`);
    }
  };

  const handleExportAll = async () => {
    if (generatedPosts.length === 0) return;

    try {
      const handle = selectedSocio?.handle?.replace('@', '') || 'post';
      const zip = new JSZip();

      // Adicionar cada imagem ao ZIP
      generatedPosts.forEach((post, index) => {
        if (post.canvasData) {
          // Remover o prefixo "data:image/png;base64," para obter apenas o base64
          const base64Data = post.canvasData.split(',')[1];
          const fileName = `post-${handle}-${index + 1}.png`;
          zip.file(fileName, base64Data, { base64: true });
        }
      });

      // Gerar o ZIP e fazer download
      toast.info('Gerando arquivo ZIP...');
      const content = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `posts-${handle}-${Date.now()}.zip`;
      saveAs(content, zipFileName);

      toast.success(`${generatedPosts.length} imagens exportadas em ${zipFileName}!`);
    } catch (error) {
      console.error('Erro ao gerar ZIP:', error);
      toast.error('Erro ao exportar imagens. Tente novamente.');
    }
  };

  const handleGenerateWithAI = async (count: 3 | 5 | 10) => {
    if (!selectedSocio) {
      toast.error('Selecione um perfil primeiro');
      return;
    }

    setIsGenerating(true);
    setIsManualMode(false); // Desativar modo manual ao usar IA

    try {
      const response = await instagramAPI.generatePosts(
        selectedSocio.profileId,
        count,
        selectedTheme || undefined
      );

      if (response.data.success) {
        const newPosts: GeneratedPost[] = response.data.data.posts.map(
          (p: { text: string; profileName: string; profileHandle: string }) => ({
            text: p.text,
          })
        );

        // Adicionar os novos posts aos existentes em vez de substituir
        setGeneratedPosts((prev) => [...prev, ...newPosts]);

        // Selecionar o primeiro post novo adicionado
        const newPostIndex = generatedPosts.length;
        setSelectedPostIndex(newPostIndex);
        setCurrentText(newPosts[0].text);

        toast.success(`${newPosts.length} posts gerados e adicionados à galeria!`);
      } else {
        toast.error('Erro ao gerar posts');
      }
    } catch (error) {
      console.error('Erro ao gerar posts:', error);
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(errorMessage || 'Erro ao gerar posts com IA');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePostClick = (index: number) => {
    setSelectedPostIndex(index);
    setCurrentText(generatedPosts[index].text);
    setIsManualMode(true); // Habilitar edição manual ao clicar em post
  };

  const handleAddNewPost = () => {
    if (!selectedSocio) {
      toast.error('Selecione um perfil primeiro');
      return;
    }

    // Adicionar novo post vazio
    const newPost: GeneratedPost = { text: '' };
    setGeneratedPosts((prev) => [...prev, newPost]);
    setSelectedPostIndex(generatedPosts.length); // Selecionar o novo post
    setCurrentText('');
    setIsManualMode(true);
    toast.success('Novo post adicionado!');
  };

  const handleDeletePost = (index: number) => {
    // Remover post da galeria
    setGeneratedPosts((prev) => prev.filter((_, i) => i !== index));

    // Ajustar seleção
    if (selectedPostIndex === index) {
      // Se estava selecionado, selecionar o anterior ou o próximo
      if (generatedPosts.length > 1) {
        const newIndex = index > 0 ? index - 1 : 0;
        setSelectedPostIndex(newIndex);
        setCurrentText(generatedPosts[newIndex]?.text || '');
      } else {
        // Era o último post
        setSelectedPostIndex(null);
        setCurrentText('');
      }
    } else if (selectedPostIndex !== null && selectedPostIndex > index) {
      // Se o post deletado estava antes do selecionado, ajustar índice
      setSelectedPostIndex(selectedPostIndex - 1);
    }

    toast.success('Post removido da galeria');
  };

  /**
   * Aplica formatação ao texto selecionado ou insere na posição do cursor
   */
  const applyFormatting = (formatType: 'bold' | 'bullet' | 'hashtag') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = currentText.substring(start, end);
    const beforeText = currentText.substring(0, start);
    const afterText = currentText.substring(end);

    let newText = '';
    let cursorPosition = start;

    switch (formatType) {
      case 'bold': {
        if (selectedText) {
          // Adicionar ** exatamente ao redor do texto selecionado (sem modificar espaços)
          newText = `${beforeText}**${selectedText}**${afterText}`;
          cursorPosition = end + 4; // Posicionar após os **
        } else {
          // Nada selecionado - inserir placeholder
          newText = `${beforeText}**texto**${afterText}`;
          cursorPosition = start + 2; // Posicionar dentro dos **
        }
        break;
      }

      case 'bullet': {
        // Adicionar bullet no início da linha
        const lineStart = currentText.lastIndexOf('\n', start - 1) + 1;
        const lineEnd = currentText.indexOf('\n', start);
        const currentLine = currentText.substring(
          lineStart,
          lineEnd === -1 ? currentText.length : lineEnd
        );

        // Se linha já tem bullet, não adicionar
        if (currentLine.trim().startsWith('•') || currentLine.trim().startsWith('-')) {
          return;
        }

        newText =
          currentText.substring(0, lineStart) +
          '• ' +
          currentLine +
          (lineEnd === -1 ? '' : currentText.substring(lineEnd));
        cursorPosition = start + 2;
        break;
      }

      case 'hashtag': {
        if (selectedText) {
          // Adicionar # antes do texto selecionado
          newText = `${beforeText}#${selectedText}${afterText}`;
          cursorPosition = end + 1;
        } else {
          // Inserir placeholder
          newText = `${beforeText}#hashtag${afterText}`;
          cursorPosition = start + 1;
        }
        break;
      }
    }

    setCurrentText(newText);

    // Restaurar foco e posição do cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  return (
    <div className="h-full flex gap-4">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0 h-full">
        <Card className="h-full flex flex-col">
          <CardHeader className="flex-shrink-0 pb-3">
            <CardTitle className="text-sm">Perfis ({socios.length})</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto space-y-2 pt-0">
            {socios.map((socio) => {
              const isSelected = selectedSocio?.id === socio.id;

              return (
                <div
                  key={socio.id}
                  onClick={() => handleSocioClick(socio)}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                    isSelected
                      ? 'bg-green-950 border-green-700'
                      : 'border-transparent hover:bg-accent'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-muted overflow-hidden flex-shrink-0">
                    <img
                      src={socio.avatar}
                      alt={socio.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.classList.add(
                          'bg-gradient-to-br',
                          'from-primary/20',
                          'to-primary/5'
                        );
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{socio.name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {socio.handle}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full gap-4">
        {/* Toolbar */}
        <div className="flex-shrink-0 p-3 bg-card rounded-lg border flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {selectedSocio && (
              <>
                <div className="px-3 py-1 bg-green-950 border border-green-700 rounded-full text-xs text-green-400 flex items-center gap-2">
                  <span>{selectedSocio.name}</span>
                  <span className="text-green-500">●</span>
                </div>

                {/* Seletor de Tema */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Tema:</span>
                  <div className="flex gap-1 bg-muted/30 rounded-md p-1">
                    <button
                      onClick={() => setTheme('light')}
                      className={`px-3 py-1 rounded transition-colors flex items-center gap-1.5 ${
                        theme === 'light'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <span>☀️</span>
                      <span>Claro</span>
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`px-3 py-1 rounded transition-colors flex items-center gap-1.5 ${
                        theme === 'dark'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <span>🌙</span>
                      <span>Escuro</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Seletor de Tema */}
            {selectedSocio && availableThemes.length > 0 && (
              <div className="flex items-center gap-2">
                <label htmlFor="theme-select" className="text-sm text-muted-foreground">
                  Tema:
                </label>
                <select
                  id="theme-select"
                  value={selectedTheme || ''}
                  onChange={(e) => setSelectedTheme(e.target.value || null)}
                  className="px-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Sem tema específico</option>
                  {availableThemes.map((theme, index) => (
                    <option key={index} value={theme}>
                      {theme}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Gerar com IA */}
            <div className="relative">
              <Button
                disabled={!selectedSocio || isGenerating}
                variant="outline"
                onClick={() => setShowAIMenu(!showAIMenu)}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Bot className="h-4 w-4 mr-2" />
                    Nexus
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
              {showAIMenu && !isGenerating && selectedSocio && (
                <div className="absolute top-full mt-2 right-0 bg-card border rounded-lg shadow-lg z-50 min-w-[160px]">
                  <button
                    onClick={() => {
                      handleGenerateWithAI(3);
                      setShowAIMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors rounded-t-lg"
                  >
                    Gerar 3 posts
                  </button>
                  <button
                    onClick={() => {
                      handleGenerateWithAI(5);
                      setShowAIMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                  >
                    Gerar 5 posts
                  </button>
                  <button
                    onClick={() => {
                      handleGenerateWithAI(10);
                      setShowAIMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors rounded-b-lg"
                  >
                    Gerar 10 posts
                  </button>
                </div>
              )}
            </div>

            {/* Exportar Todos */}
            {generatedPosts.length > 0 && (
              <Button onClick={handleExportAll} className="bg-green-600 hover:bg-green-700 text-white">
                <Download className="h-4 w-4 mr-2" />
                Exportar Todos ({generatedPosts.length})
              </Button>
            )}
          </div>
        </div>

        {/* Área de Preview/Galeria */}
        <div className="flex-1 flex items-center justify-center rounded-lg overflow-hidden min-h-0">
          {!selectedSocio || generatedPosts.length === 0 ? (
            <div className="text-center text-muted-foreground">
              <div className="text-sm">
                {!selectedSocio
                  ? 'Selecione um perfil para começar'
                  : 'Digite um texto ou gere posts com o Nexus'}
              </div>
            </div>
          ) : (
            /* Galeria unificada (1 ou mais posts) */
            <div className="w-full h-full overflow-y-auto p-4">
              <div className="grid grid-cols-3 gap-4">
                {generatedPosts.map((post, index) => (
                  <div
                    key={index}
                    className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      selectedPostIndex === index
                        ? 'border-green-500 shadow-lg scale-105'
                        : 'border-transparent hover:border-primary/50'
                    }`}
                    style={{ aspectRatio: '4/5' }}
                    onClick={() => handlePostClick(index)}
                  >
                    {post.canvasData ? (
                      <img
                        src={post.canvasData}
                        alt={`Post ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {/* Botões de ação */}
                    <div className="absolute top-2 right-2 flex gap-2">
                      {/* Botão expandir */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedImage(post.canvasData || null);
                        }}
                        className="p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                        title="Expandir imagem"
                      >
                        <ZoomIn className="h-4 w-4 text-white" />
                      </button>
                      {/* Botão download */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportSpecific(index);
                        }}
                        className="p-2 bg-green-500/80 rounded-full hover:bg-green-600 transition-colors"
                        title="Salvar esta imagem"
                      >
                        <Download className="h-4 w-4 text-white" />
                      </button>
                      {/* Botão deletar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePost(index);
                        }}
                        className="p-2 bg-red-500/80 rounded-full hover:bg-red-600 transition-colors"
                        title="Remover post"
                      >
                        <Trash2 className="h-4 w-4 text-white" />
                      </button>
                    </div>
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
                      Post {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Texto Input com Barra de Ferramentas */}
        <div className="flex-shrink-0 bg-card rounded-lg border">
          {/* Barra de Ferramentas de Formatação */}
          <div className="flex items-center justify-between gap-1 p-2 border-b bg-muted/30">
            {/* Botões de Formatação (esquerda) */}
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => applyFormatting('bold')}
                className="h-8 w-8 p-0"
                title="Negrito (selecione o texto e clique)"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => applyFormatting('bullet')}
                className="h-8 w-8 p-0"
                title="Adicionar bullet point"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => applyFormatting('hashtag')}
                className="h-8 w-8 p-0"
                title="Adicionar hashtag"
              >
                <Hash className="h-4 w-4" />
              </Button>
            </div>

            {/* Botão Adicionar Post (direita) */}
            <button
              onClick={handleAddNewPost}
              disabled={!selectedSocio}
              className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 px-2"
              title="Adicionar novo post vazio"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Novo Post</span>
            </button>
          </div>

          {/* Textarea */}
          <Textarea
            ref={textareaRef}
            value={currentText}
            onChange={(e) => setCurrentText(e.target.value)}
            placeholder="Digite o texto do post..."
            className="w-full resize-none border-0 focus-visible:ring-0 min-h-[100px]"
          />
        </div>
      </div>

      {/* Modal de Imagem Expandida */}
      <Dialog open={!!expandedImage} onOpenChange={() => setExpandedImage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-2">
          {expandedImage && (
            <div className="flex items-center justify-center w-full h-full">
              <img
                src={expandedImage}
                alt="Post expandido"
                className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
